import { useCallback, useMemo, useState } from 'react';

import type { GridColDef, GridInitialState } from '@mui/x-data-grid';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import debounce from 'lodash/debounce';

import type { StorageManager } from '../../types/storage-manager';

import { getFeaturesToPersist } from './features-to-persist-presets';
import { filterPersistedState } from './filter-persisted-state';
import { hideNewFields } from './hide-new-fields';
import { usePersistedGridEffects } from './use-persisted-grid-effects';
import { usePersistedGridState } from './use-persisted-grid-state';

const debounceWaitTime = 100;

export const usePersistedGrid = ({
  persistencyOptions,
  gridOptions,
  featuresPreset = 'default',
}: {
  persistencyOptions: {
    storageKey: string;
    storageOptions?: any;
    persistBeforeunload?: boolean;
    featuresToPersist?: Record<string, any>;
    storageManager?: StorageManager;
  };
  gridOptions: {
    columns: GridColDef[];
    defaultState: GridInitialState;
  };
  featuresPreset?: string;
}) => {
  const {
    storageKey,
    storageOptions,
    storageManager,
    persistBeforeunload,
    featuresToPersist = getFeaturesToPersist(featuresPreset),
  } = persistencyOptions;

  const { defaultState, columns } = gridOptions;
  const calcDefaultState = hideNewFields(columns, defaultState, defaultState);

  const apiRef = useGridApiRef();
  const persistedGridState = usePersistedGridState(
    storageKey,
    storageManager,
    storageOptions,
  );

  const [isLoading, setIsLoading] = useState(true);
  const [persistedInitialState, setPersistedInitialState] =
    useState<GridInitialState>();

  if (!storageKey) {
    throw new Error('The "storageKey" is required in persistencyOptions.');
  }
  if (!columns || columns.length === 0) {
    throw new Error('The "columns" array is required in gridOptions.');
  }

  usePersistedGridEffects({
    apiRef,
    columns,
    isLoading,
    defaultState: calcDefaultState,
    featuresToPersist,
    persistedGridState,
    persistBeforeunload,
    persistedInitialState,
    setIsLoading,
    setPersistedInitialState,
  });

  const handleChangeState = useCallback(() => {
    if (!persistedInitialState) return;

    if (!apiRef.current) {
      throw new Error(
        'The API ref is not available. Make sure the component is mounted.',
      );
    }

    const currentState = apiRef.current.exportState();
    const filteredState = filterPersistedState(currentState, featuresToPersist);

    persistedGridState.set(filteredState);
  }, [apiRef, featuresToPersist, persistedInitialState, persistedGridState]);

  const debouncedHandleChangeState = useMemo(
    () => debounce(handleChangeState, debounceWaitTime),
    [handleChangeState],
  );

  return {
    apiRef,
    columns,
    isLoading,
    persistedInitialState,
    initialState: isLoading ? calcDefaultState : undefined,
    handleChangeState: debouncedHandleChangeState,
  };
};
